/**
 * usePhotos — fetches the photo list for a vehicle from GET /v1/photos?vehicleId=:id.
 *
 * Uses TanStack Query (server state, not Firestore) since photos come from
 * the gg-api backend, not Firestore.
 *
 * Returns loading, error, and data consistent with other hooks on this page.
 *
 * Slice 2/3 fields: publicConfirmed and derivativeStatus are declared optional
 * (with .optional()) so the schema parses cleanly against both the current
 * backend (which may not yet return them) and the Slice-2 backend. Flag raised
 * in PR description — a small backend addition is needed for full functionality.
 *
 * Spec 011: the client only ever needs `status` to know when a photo is
 * usable as a before/after reference — the server resolves the id to its
 * authoritative {photoId, sha256} internally once `status === 'committed'`
 * (a photo only reaches "committed" after graydon's async strip-worker
 * finishes). The client never sees or needs the sha256 itself, so it isn't
 * declared here. `PhotoSchema`/`PhotoListSchema` are exported so
 * useServiceQueue.ts's drain can poll this same endpoint directly (outside
 * TanStack Query's cache) to wait for that status transition.
 */

import { apiFetch } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const PhotoSchema = z.object({
  photoId: z.string(),
  url: z.string(),
  contentType: z.string(),
  status: z.enum(['pending', 'committed', 'failed']),
  capturedAt: z.string().optional(),
  caption: z.string().optional(),
  createdAt: z.string(),
  // Slice 2/3 fields — may be absent if backend not yet updated (see PR flag).
  publicConfirmed: z.boolean().optional(),
  derivativeStatus: z.enum(['rendering', 'ready', 'failed']).optional(),
});

export const PhotoListSchema = z.object({
  photos: z.array(PhotoSchema),
});

export type Photo = z.infer<typeof PhotoSchema>;

export function photoKeys(vehicleId: string) {
  return ['photos', vehicleId] as const;
}

export function usePhotos(vehicleId: string | null) {
  return useQuery({
    queryKey: vehicleId ? photoKeys(vehicleId) : ['photos', '__disabled'],
    enabled: !!vehicleId,
    queryFn: async () => {
      const data = await apiFetch(`/v1/photos?vehicleId=${encodeURIComponent(vehicleId!)}`, {
        schema: PhotoListSchema,
      });
      return data.photos;
    },
    staleTime: 30_000,
  });
}
